import React from 'react';
import Seo from '../components/Seo';
import { organizationLd, faqLd, breadcrumbLd } from '../lib/seo';
import { Link } from 'react-router-dom';
import { Factory, ShieldCheck, Globe, Award, Sparkles, Package, ArrowRight, FileText, MessageCircle, Check } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { products, certifications, stats } from '../mock/mock';
import { faqs } from '../lib/seo';
import { openWhatsApp } from '../lib/wa';

const reasons = [
  { icon: Factory, title: 'Our own mill at Port Qasim', text: 'We mill in-house rather than trade third-party stock — giving buyers direct-from-source quality, traceability and control over every consignment.' },
  { icon: ShieldCheck, title: 'REAP-registered & certified', text: 'A registered member of the Rice Exporters Association of Pakistan, operating to ISO 9001, ISO 22000, HACCP and Halal standards.' },
  { icon: Globe, title: '30+ export markets', text: 'Trusted by importers across the Gulf, East & West Africa, Southeast Asia, Europe and the Americas since 1999.' },
  { icon: Sparkles, title: 'Sortex-clean quality', text: 'Modern milling and colour-sorting deliver clean, uniform grain to your exact broken %, moisture and purity specification.' },
  { icon: Package, title: 'Flexible packaging', text: 'PP & jute bags, non-woven and consumer retail packs, jumbo bags and full private-label packing to your brand.' },
  { icon: Award, title: '25+ years of experience', text: 'A family-run exporter with decades of relationships, references and repeat buyers around the world.' },
];

const steps = [
  { n: '01', title: 'Share your specification', text: 'Tell us variety, grade, broken %, packaging and destination port.' },
  { n: '02', title: 'Receive quote & samples', text: 'We respond with FOB Port Qasim or CIF pricing and can send samples.' },
  { n: '03', title: 'Inspection & documentation', text: 'SGS / Bureau Veritas inspection on request, with full export documents.' },
  { n: '04', title: 'Loading & shipment', text: 'Container or bulk vessel loading from Port Qasim, tracked to delivery.' },
];

const RiceExportersPakistan = () => {
  const riceProducts = products.filter((p) => p.category === 'basmati' || p.category === 'non-basmati');

  return (
    <div>
      <Seo
        title="Rice Exporters in Pakistan — Basmati & Non-Basmati Rice Exporter"
        description="Kassam Trading Company is a leading rice exporter in Pakistan — REAP-registered miller of 1121 & 1509 basmati and IRRI-6, PK-386 & C-9 non-basmati rice. FOB Port Qasim, CIF worldwide since 1999."
        path="/rice-exporters-pakistan"
        jsonLd={[
          organizationLd,
          faqLd,
          breadcrumbLd([
            { name: 'Home', path: '/' },
            { name: 'Rice Exporters in Pakistan', path: '/rice-exporters-pakistan' },
          ]),
        ]}
      />
      <PageHero
        title="Rice Exporters in Pakistan"
        subtitle="Kassam Trading Company (KTC) is a leading, REAP-registered rice exporter in Pakistan — milling and shipping premium basmati and economical non-basmati rice to 30+ countries since 1999."
        crumb="Rice Exporters in Pakistan"
      />

      {/* Intro */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-12 items-start">
        <div className="lg:col-span-2">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ktc-green leading-tight">
              Premium Pakistani rice, direct from our mill to your port
            </h2>
            <p className="text-ktc-charcoal/75 mt-5 leading-relaxed">
              As one of Pakistan&apos;s established rice exporters, Kassam Trading Company supplies the full spectrum of
              Pakistani rice — from world-famous <Link to="/products/1121-sella-basmati-rice" className="text-ktc-gold font-medium hover:underline">1121 Sella Basmati</Link> and
              {' '}<Link to="/products/1509-sella-basmati-rice" className="text-ktc-gold font-medium hover:underline">1509 Basmati</Link> to
              economical <Link to="/products/irri-6-long-grain-white-rice" className="text-ktc-gold font-medium hover:underline">IRRI-6</Link> and
              {' '}<Link to="/products/pk-386-white-rice" className="text-ktc-gold font-medium hover:underline">PK-386</Link> non-basmati rice.
            </p>
            <p className="text-ktc-charcoal/75 mt-4 leading-relaxed">
              Because we operate our own rice mill at Port Qasim, Karachi, we control quality from paddy to packing — which is
              why importers searching for the best rice exporter in Pakistan choose KTC for consistent grain, honest specifications
              and reliable shipments. We ship FOB Port Qasim with CIF and C&amp;F options to most major ports worldwide.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-7 py-3.5 rounded-md transition-colors" data-testid="landing-quote-btn">
                <FileText size={18} /> Request a Quote
              </Link>
              <button type="button" onClick={() => openWhatsApp()} data-testid="landing-whatsapp-btn" className="inline-flex items-center gap-2 border border-ktc-green/25 hover:border-ktc-green text-ktc-green font-medium px-7 py-3.5 rounded-md transition-colors">
                <MessageCircle size={18} /> WhatsApp Us
              </button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={100}>
          <div className="grid grid-cols-2 gap-px bg-ktc-cream rounded-2xl overflow-hidden border border-ktc-cream">
            {stats.map((s) => (
              <div key={s.label} className="bg-ktc-ivoryLight p-6">
                <div className="font-serif text-3xl font-semibold text-ktc-green">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-ktc-charcoal/55 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Why KTC */}
      <section className="bg-ktc-ivoryLight border-y border-ktc-cream">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.2em] text-ktc-gold font-semibold">Why choose KTC</span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ktc-green mt-3 max-w-2xl">
              Among the most trusted rice exporters in Pakistan
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {reasons.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 70}>
                <div className="bg-white rounded-xl border border-ktc-cream p-7 h-full card-lift">
                  <div className="h-11 w-11 grid place-items-center rounded-lg bg-ktc-green/10 text-ktc-green">
                    <r.icon size={22} />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-ktc-green mt-4">{r.title}</h3>
                  <p className="text-sm text-ktc-charcoal/70 mt-2 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Rice range */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ktc-green">Our rice export range</h2>
          <p className="text-ktc-charcoal/70 mt-3 max-w-2xl">Basmati and non-basmati rice in Sella, Steam, white and broken grades — milled and packed to your specification.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {riceProducts.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 60}>
              <Link to={`/products/${p.slug}`} className="group block bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift h-full">
                <div className="img-zoom h-44"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <h3 className="font-serif text-lg font-semibold text-ktc-green leading-snug">{p.name}</h3>
                  <p className="text-sm text-ktc-charcoal/60 mt-1.5 line-clamp-2">{p.short}</p>
                  <span className="inline-flex items-center gap-1.5 text-ktc-gold font-medium text-sm mt-3 group-hover:gap-2.5 transition-all">View details <ArrowRight size={14} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-ktc-green font-semibold hover:gap-3 transition-all">See all products <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Process */}
      <section className="bg-ktc-green text-ktc-ivory">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold">How to order rice from Pakistan</h2>
            <p className="text-ktc-ivory/75 mt-3 max-w-2xl">A simple, transparent process from enquiry to delivery.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="border-t-2 border-ktc-gold/60 pt-5">
                  <div className="font-serif text-4xl text-ktc-goldLight/80">{s.n}</div>
                  <h3 className="font-serif text-xl font-semibold mt-2">{s.title}</h3>
                  <p className="text-sm text-ktc-ivory/70 mt-2 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-ktc-green">Certified &amp; compliant</h2>
              <p className="text-ktc-charcoal/70 mt-2 max-w-xl">Documented quality your buyers and their regulators can trust.</p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {certifications.map((c) => (
                <span key={c.abbr} className="flex items-center gap-1.5 text-sm bg-ktc-cream/70 text-ktc-green font-medium px-3.5 py-2 rounded-full">
                  <Check size={15} className="text-ktc-gold" /> {c.abbr}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-6">
            <Link to="/certificates" className="inline-flex items-center gap-2 text-ktc-green font-semibold hover:gap-3 transition-all">View our certificates <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="bg-ktc-ivoryLight border-t border-ktc-cream">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-ktc-green text-center">Rice export FAQs</h2>
          </Reveal>
          <div className="mt-8 divide-y divide-ktc-green/10 border-y border-ktc-green/10">
            {faqs.slice(0, 5).map((f, i) => (
              <div key={i} className="py-5">
                <h3 className="font-serif text-lg text-ktc-green">{f.q}</h3>
                <p className="text-ktc-charcoal/75 mt-2 leading-relaxed text-[15px]">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md transition-colors">
              Request a Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RiceExportersPakistan;
